import {Amplify} from 'aws-amplify';
import {decodeJWT} from 'aws-amplify/auth';
import {
  type AuthUser,
  confirmResetPassword as confirmResetUserPassword,
  confirmSignIn as confirmSignInFromAmplify,
  type ConfirmSignInInput,
  type ConfirmSignInOutput,
  fetchAuthSession as fetchAuthSessionFromAmplify,
  getCurrentUser as getCurrentUserFromAmplify,
  signIn as signInFromAmplify,
  type SignInInput,
  type SignInOutput,
  signOut as signOutFromAmplify,
} from 'aws-amplify/auth';
import {
  cognitoUserPoolsTokenProvider,
  DefaultTokenStore,
} from 'aws-amplify/auth/cognito';
import {sessionStorage} from 'aws-amplify/utils';

import {getDeviceUUID} from '@/utils/getDeviceUUID';

import type {UserGroup} from './graphqlHooks';

const cognitoConfig = {
  Cognito: {
    userPoolClientId: window.AWS_USER_POOL_CLIENT_ID,
    userPoolId: window.AWS_USER_POOL_ID,
  },
};

const tokenStore = new DefaultTokenStore();

Amplify.configure({
  Auth: cognitoConfig,
});

cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);

export const getCurrentUser = async () => {
  try {
    return await getCurrentUserFromAmplify();
  } catch {
    return null;
  }
};

export const signIn = async (input: SignInInput): Promise<SignInOutput> => {
  try {
    return await signInFromAmplify({
      ...input,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    });
  } catch (error) {
    if (error instanceof Error) {
      error.name === 'UserNotFoundException';
      throw new Error('INVALID_PASSWORD_OR_USERNAME');
    }
    throw error;
  }
};

export const confirmSignIn = async (
  input: ConfirmSignInInput
): Promise<ConfirmSignInOutput> => {
  try {
    return await confirmSignInFromAmplify(input);
  } catch (error) {
    if (error instanceof Error) throw error;
    return error as ConfirmSignInOutput;
  }
};

export const signOut = async () => {
  await signOutFromAmplify();
};

export const setTokens = async (tokens: Record<string, string>) => {
  tokenStore.setAuthConfig(cognitoConfig);
  tokenStore.setKeyValueStorage(sessionStorage);

  const accessToken = decodeJWT(tokens.accessToken);
  const accessTokenIssuedAtInMillis = (accessToken.payload.iat || 0) * 1000;
  const currentTime = new Date().getTime();
  await tokenStore.storeTokens({
    accessToken,
    refreshToken: tokens.refreshToken,
    idToken: decodeJWT(tokens.idToken),
    username: accessToken.payload['username'] as string,
    clockDrift:
      accessTokenIssuedAtInMillis > 0
        ? accessTokenIssuedAtInMillis - currentTime
        : 0,
  });
};

export const scanSignIn = async (token: string) => {
  const response = await fetch(window.AWS_AUTH_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      identityToken: token,
      uuid: getDeviceUUID(),
    }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text);
  }
  const tokens = (await response.json()) as Record<string, string>;
  await setTokens(tokens);
};

export const getJWT = async () => {
  const session = await fetchAuthSessionFromAmplify();
  return session?.tokens?.idToken;
};

export const DEV_GROUPS_STORAGE_KEY = 'dev_groups_override';

export const getGroups = async () => {
  if (import.meta.env.DEV) {
    const override = window.sessionStorage.getItem(DEV_GROUPS_STORAGE_KEY);
    if (override) return JSON.parse(override) as UserGroup[];
  }
  const token = await getJWT();
  return (token?.payload['cognito:groups'] || []) as UserGroup[];
};

export const getCurrentUserQueryOptions = {
  queryKey: ['CurrentUser'],
  queryFn: getCurrentUser,
};

export const getGroupsQueryOptions = {
  queryKey: ['GetGroups'],
  queryFn: getGroups,
};

export const signInMutationOptions = {
  mutationKey: ['SignIn'],
  mutationFn: signIn,
};

export const confirmSignInMutationOptions = {
  mutationKey: ['ConfirmSignIn'],
  mutationFn: confirmSignIn,
};

export const confirmResetMutationOptions = {
  mutationKey: ['ConfirmReset'],
  mutationFn: confirmResetUserPassword,
};

export const scanSignInMutationOptions = {
  mutationKey: ['ScanSignIn'],
  mutationFn: scanSignIn,
};

export const signOutMutationOptions = {
  mutationKey: ['SignOut'],
  mutationFn: signOut,
};

export type {AuthUser, SignInOutput};
